import React, { useState, useContext } from 'react';
import { Button, Container, Form, Header, SpaceBetween } from '@amzn/awsui-components-core';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { DocumentFeedbackStrategy } from './strategies/DocumentFeedbackStrategy';
import { AppContext } from '../../contexts/AppContext';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';

interface UpdateRequestFeedbackFormProps {
  readonly contentId: string;
  readonly onCancel: () => void;
  readonly onSubmitSuccess: () => void;
}

const mplService = MplServiceClientFactory.create();
const strategy = new DocumentFeedbackStrategy();

const UpdateRequestFeedbackForm = ({
  contentId,
  onCancel,
  onSubmitSuccess,
}: UpdateRequestFeedbackFormProps) => {
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [improvementFeedbackText, setImprovementFeedbackText] = useState('');
  const [comment, setComment] = useState('');
  const [actionFeedbackText, setActionFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDescriptionValid, setIsDescriptionValid] = useState(false);

  const appContext = useContext(AppContext);
  const flashbarMessageBroker = FlashbarMessageBroker.create();

  const isFormValid = !!selectedIssue && isDescriptionValid;

  const state = {
    selectedIssue,
    improvementFeedbackText,
    comment,
    actionFeedbackText,
    isFormValid,
  };
  const handlers = {
    setSelectedIssue,
    setImprovementFeedbackText,
    setComment,
    setActionFeedbackText,
    onDescriptionValidationChange: setIsDescriptionValid,
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    setIsSubmitting(true);
    try {
      await mplService.sendPublicationFeedback(
        contentId,
        // @ts-expect-error TS2339 - strict migration
        selectedIssue?.value,
        improvementFeedbackText,
        actionFeedbackText,
        comment,
      );
      flashbarMessageBroker.pushSuccessMessage(
        'We have successfully received your feedback and have reported to the author.',
        0,
        'Thank you for your feedback',
      );
      onSubmitSuccess();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      flashbarMessageBroker.pushErrorMessage('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isSubmitting}
            disabled={!isFormValid}
            onClick={handleSubmit}
          >
            Submit feedback
          </Button>
        </SpaceBetween>
      }
      header={<Header variant="h3">{strategy.getHeaderText()}</Header>}
    >
      <Container header={<Header variant="h3">Feedback</Header>}>
        {/* @ts-expect-error TS2345 - strict migration */}
        {strategy.renderFormFields(state, handlers)}
      </Container>
    </Form>
  );
};

export default UpdateRequestFeedbackForm;
